import { COMPILED_COLLISION_CELLS_PER_TILE, frameId, propFeet, type CompileContext, type MapDocument, type PropInstance, type WorldDocument } from "daedalus-core";
import { nearestStandable } from "../movement/nearest.ts";
import { COLLISION_CELL, SIXTEENTHS, canStand, type CollisionGrid } from "../movement/step.ts";
import { shrineGround, shrineStandFrom } from "../shrines/ground.ts";
import { SHRINE_STAND_RADIUS_TILES } from "../shrines/respawn.ts";
import { compiledGridOf } from "./map-grid.ts";
import { SHRINE, SPAWN_POINT, WAYPOINT } from "./type-names.ts";

/**
 * Whether a shrine's standing spot can be walked to from the map's Spawn Point (ADR-096 decision 41): a flood of the
 * map's compiled collision grid by a character's feet box, one collision cell a step, run where a document is
 * compiled and never on an edit, since it resolves the map's terrain. The grid is the one a published chunk carries,
 * terrain, plain props and collision layers, with the cells every thing blocks whatever a player does marked over it
 * (`stampBlockingThings`, ADR-096's amendment of 2026-10-13): a gate a player can open counts only its posts, and a
 * well, a dummy, a chest, another shrine and the shrine's own stone block as they stand. So a shrine shut in by things
 * alone is reported. A key no player can come by, and anything that moves, it does not know of.
 */

/**
 * One shrine or waypoint a character cannot reach: `cut off` when its standing spot exists and the spawn cannot walk
 * to it, `buried` when nowhere within `SHRINE_STAND_RADIUS_TILES` of its base can be stood on, which the map server
 * leaves out of the respawn list, or of the travel list for a waypoint (`waypoint` is present, `true`, only then).
 */
export interface ShrineReachReport {
  readonly mapId: string;
  readonly layerId: string;
  readonly propId: string;
  readonly name: string;
  readonly reason: "cut off" | "buried";
  readonly waypoint?: true;
}

/**
 * How far from a Spawn Point a spot to start the flood from is looked for, in collision cells: the spawn's own tile
 * and those beside it.
 */
const SPAWN_SEARCH_CELLS = 2 * COMPILED_COLLISION_CELLS_PER_TILE;

/**
 * The lattice points of feet positions a character can reach from a start, one collision cell a step along either
 * axis: a point is `(column, row)` for a feet centre x of `column` cells and a feet line y of `row` cells.
 *
 * @param grid The collision grid.
 * @param start The feet position the flood starts from, in sixteenths, on the lattice.
 * @returns One byte a lattice point, row-major over `(grid.width + 1)` by `(grid.height + 1)` points, 1 where reached.
 */
function reachedFrom(grid: CollisionGrid, start: { readonly x: number; readonly y: number }): Uint8Array {
  const width = grid.width + 1;
  const height = grid.height + 1;
  const reached = new Uint8Array(width * height);
  const queue = new Int32Array(width * height);
  let head = 0;
  let tail = 0;
  const first = Math.round(start.y / COLLISION_CELL) * width + Math.round(start.x / COLLISION_CELL);
  reached[first] = 1;
  queue[tail] = first;
  tail += 1;
  while (head < tail) {
    const at = queue[head] as number;
    head += 1;
    const column = at % width;
    const row = (at - column) / width;
    for (const [dx, dy] of [[1, 0], [-1, 0], [0, 1], [0, -1]] as const) {
      const nextColumn = column + dx;
      const nextRow = row + dy;
      if (nextColumn < 0 || nextRow < 0 || nextColumn >= width || nextRow >= height) {
        continue;
      }
      const next = nextRow * width + nextColumn;
      if (reached[next] === 0 && canStand(nextColumn * COLLISION_CELL, nextRow * COLLISION_CELL, grid)) {
        reached[next] = 1;
        queue[tail] = next;
        tail += 1;
      }
    }
  }
  return reached;
}

/**
 * The point a shrine's search for a standing spot starts from, in sixteenths.
 *
 * @param prop The shrine.
 * @param context The compile context, for the placed frame's footprint.
 * @param tileSize The world's tile size in pixels.
 * @returns The point, or null when the context holds no footprint for the frame.
 */
function standFrom(prop: PropInstance, context: Pick<CompileContext, "collision">, tileSize: number): { x: number; y: number } | null {
  const footprint = context.collision.footprints?.[frameId(prop.frame.tileset, null, prop.frame.tile)];
  if (footprint === undefined) {
    return null;
  }
  const feet = propFeet(prop, footprint.w, tileSize);
  const ground = shrineGround({ x: feet.x * SIXTEENTHS, y: feet.y * SIXTEENTHS, footprint, flipX: prop.flipX, flipY: prop.flipY }, tileSize);
  return shrineStandFrom(ground.x, ground.y);
}

/**
 * The shrines of one map a character cannot reach.
 *
 * @param world The document.
 * @param map One of its maps.
 * @param context The compile context.
 * @returns The reports, in document order; none for a map with no shrine or no Spawn Point.
 */
function unreachableOn(world: WorldDocument, map: MapDocument, context: CompileContext): ShrineReachReport[] {
  const shrines = map.layers.flatMap((layer) => (layer.kind === "prop" ? layer.props.filter((prop) => prop.behaviour?.type === SHRINE || prop.behaviour?.type === WAYPOINT).map((prop) => ({ layerId: layer.id, prop })) : []));
  const spawn = map.layers.flatMap((layer) => (layer.kind === "entity" ? layer.entities : [])).find((entity) => entity.type === SPAWN_POINT);
  if (shrines.length === 0 || spawn === undefined) {
    return [];
  }
  const grid = compiledGridOf(world, map, context);
  const start = nearestStandable(Math.round(spawn.x * SIXTEENTHS), Math.round(spawn.y * SIXTEENTHS), grid, SPAWN_SEARCH_CELLS);
  const reached = start === null ? null : reachedFrom(grid, start);
  const reports: ShrineReachReport[] = [];
  for (const { layerId, prop } of shrines) {
    const from = standFrom(prop, context, world.tileSize);
    if (from === null) {
      continue;
    }
    const name = prop.behaviour?.fields["name"];
    const report = { mapId: map.id, layerId, propId: prop.id, name: typeof name === "string" ? name : "", ...(prop.behaviour?.type === WAYPOINT ? { waypoint: true as const } : {}) };
    const spot = nearestStandable(from.x, from.y, grid, SHRINE_STAND_RADIUS_TILES * COMPILED_COLLISION_CELLS_PER_TILE);
    if (spot === null) {
      reports.push({ ...report, reason: "buried" });
    } else if (reached === null || reached[Math.round(spot.y / COLLISION_CELL) * (grid.width + 1) + Math.round(spot.x / COLLISION_CELL)] === 0) {
      reports.push({ ...report, reason: "cut off" });
    }
  }
  return reports;
}

/**
 * The shrines and waypoints of a document a character cannot reach from its map's Spawn Point.
 *
 * @param world The document, upgraded and valid, as a compile holds it.
 * @param context The compile context.
 * @returns One report per such shrine, map by map in document order.
 * @throws Error when a map does not resolve, as `resolveMap` throws, or a thing's state cannot be laid, as
 *   `stampBlockingThings` throws.
 */
export function unreachableShrines(world: WorldDocument, context: CompileContext): ShrineReachReport[] {
  return world.maps.flatMap((map) => unreachableOn(world, map, context));
}

/**
 * One line for an unreachable shrine, for a compile's warnings.
 *
 * @param report The report.
 * @returns The line: what is wrong, then `; ` and what to do.
 */
export function describeShrineReach(report: ShrineReachReport): string {
  const noun = report.waypoint === true ? "waypoint" : "shrine";
  const called = `${noun} ${report.propId} ${JSON.stringify(report.name)} (map ${report.mapId}, layer ${report.layerId})`;
  return report.reason === "buried"
    ? `${called}: there is nowhere to stand within ${SHRINE_STAND_RADIUS_TILES} tiles of its base, so the game leaves it out of the ${report.waypoint === true ? "travel" : "respawn"} list; clear the ground south of it or move the ${noun}`
    : `${called}: its standing spot cannot be walked to from the Spawn Point; open a path to it or move the ${noun}`;
}
